import { apiFetch } from '../utils/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

export interface SafetyStatus {
  trading_mode: string;
  live_trading_enabled: boolean;
  toss_read_only_enabled: boolean;
  toss_configured: boolean;
  toss_us_live_enabled: boolean;
  toss_kr_live_enabled: boolean;
  toss_live_market: 'US' | 'KR';
  toss_kill_switch?: {
    global: boolean; us: boolean; kr: boolean; symbol_count?: number;
  };
  toss_order_circuit_breaker?: { threshold: number; window_seconds: number };
  stock_live_route: 'toss' | 'alpaca_paper';
}

export interface UsMarketStatus {
  stage: string;
  status: string;
  currency: 'USD';
  live_watchlist?: {
    source: string | null;
    expected_source: string;
    count: number;
    tickers: string[];
    updated_at: string | null;
    ready: boolean;
    error?: string;
  };
  provider_observations: number;
  batch_count: number;
  latest_batch: string | null;
  latest_observed_at: string | null;
  latest_session: string | null;
  latest_batch_rows: number;
  symbols: string[];
  providers: string[];
}

export interface KrMarketStatus {
  stage: string;
  status: string;
  currency: 'KRW';
  run_count: number;
  latest_run_id: string | null;
  latest_run_status?: string;
  strategy_profile?: string;
  cost_model_version?: string;
  dataset_hash?: string | null;
  created_at?: string;
  decisions: number;
  orders: number;
  fills: number;
  latest_equity?: number | null;
  performance_gate?: {
    status: 'passed' | 'sample_not_ready';
    passed: boolean;
    thresholds?: {
      min_universe: number;
      min_decisions: number;
      min_buy: number;
      min_sell: number;
      min_fills: number;
    };
    qualified_run_count: number;
    latest_qualified_run_id: string | null;
    totals: {
      decisions: number;
      BUY: number;
      SELL: number;
      HOLD: number;
      fills: number;
    };
    blockers: string[];
  };
  live_shadow?: {
    source?: string;
    recent_count: number;
    latest_shadow_at: string | null;
    latest_symbols?: string[];
    ready: boolean;
  };
  live_summary?: {
    hours: number;
    orders: {
      count: number;
      by_status: Record<string, number>;
      open_count: number;
      filled_count: number;
      latest_order_at: string | null;
    };
    holdings: {
      count: number;
      managed_existing_count: number;
      strategy_managed_count: number;
      total_market_value: number;
      total_unrealized_pl: number;
      latest_observed_at: string | null;
      currency: string;
    };
    shadow_live_comparison: {
      status: 'no_live_orders' | 'aligned' | 'diverged';
      alert_required: boolean;
      message: string;
      live_order_symbols: string[];
      shadow_symbols: string[];
      divergent_symbols: string[];
    };
  };
  performance_gate_passed: boolean;
  live_readiness?: {
    status: 'blocked' | 'ready_locked';
    watchlist: { source: string; count: number; display_name_count: number };
    ownership: {
      external_observed: number;
      managed_existing: number;
      strategy_managed: number;
      excluded: number;
      total: number;
    };
    toss_live_market: 'US' | 'KR';
    kr_switch_locked: boolean;
    blockers: string[];
  };
}

export interface TransitionGate {
  key: string;
  label: string;
  status: 'complete' | 'technical_complete' | 'in_progress' | 'pending' | 'locked' | 'ready_locked';
}

export interface CrossMarketStatus {
  generated_at: string;
  safety: SafetyStatus;
  markets: { US: UsMarketStatus; KR: KrMarketStatus };
  gates: TransitionGate[];
  analyst: {
    mode: 'shadow_only';
    order_path_changed: false;
    description: string;
  };
}

export async function fetchCrossMarketStatus(): Promise<CrossMarketStatus> {
  const response = await apiFetch(`${API_BASE_URL}/api/operations/cross-market`);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`시스템 현황 조회 실패 (${response.status}): ${text}`);
  }
  return response.json();
}
